// Редактор распределений: каждый предмет × каждая комната по всем
// источникам — из настоящей симуляции (`spusk-sim --source assets` с
// каталогом модулей, CONTRACT.md §1d), и рычаги прямо у клетки. Правки —
// оверрайды CLI в общем черновике; в ассеты ничего не пишется (ADR-0023),
// «было → стало» копируется для инспектора.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CATEGORY_LABELS,
  SOURCE_LABELS,
  ammoLevers,
  budgetLevers,
  changeLines,
  changesText,
  currentCategory,
  currentValue,
  dropStackLevers,
  formatValue,
  itemLabel,
  matrixView,
  normalizeMatrix,
  normalizeSchema,
  overridesFor,
  preferenceBonusField,
  weightLevers,
  withEdit,
  type EditorSchema,
  type Edits,
  type Metric,
  type RoomMatrix,
  type SchemaField,
} from '../lib/matrix';

const RUNS = [500, 1000, 2000] as const;
const DEBOUNCE_MS = 500;
const METRICS: Array<{ id: Metric; label: string; hint: string }> = [
  { id: 'share', label: 'Шанс за забег', hint: 'доля забегов, где предмет был в этой комнате' },
  { id: 'shareWhenPresent', label: 'Шанс, если комната есть', hint: 'та же доля среди забегов, где комната выпала' },
  { id: 'mean', label: 'Среднее за забег', hint: 'сколько штук в среднем за забег' },
];

async function api<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, { credentials: 'same-origin', ...init });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body.error ?? `Ошибка ${response.status}`) as Error & { field?: string };
    if (typeof body.field === 'string') error.field = body.field;
    throw error;
  }
  return body as T;
}

const pct = (share: number) => `${(share * 100).toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%`;

function FieldSlider({ field, edits, onEdit, label, failed }: {
  field: SchemaField; edits: Edits; onEdit: (key: string, value: number, original: number) => void; label: string; failed: boolean;
}) {
  const value = currentValue(field, edits);
  const changed = value !== field.value;
  return <div className={`lever${changed ? ' changed' : ''}${failed ? ' failed' : ''}`}>
    <div className="lever-head"><span>{label}</span>
      <strong>{value}{changed && <small> ← {field.value}</small>}</strong></div>
    <input type="range" min={field.min} max={field.max} step={1} value={value} aria-label={field.key}
      onChange={(event) => onEdit(field.key, Number(event.target.value), field.value)} />
    <small className="lever-key">{field.key}</small>
  </div>;
}

export function Workspace({ email = 'admin' }: { email?: string }) {
  const [budgets, setBudgets] = useState<Array<{ name: string; displayName: string | null }>>([]);
  const [budget, setBudget] = useState<string | null>(null);
  const [runs, setRuns] = useState<number>(1000);
  const [schema, setSchema] = useState<EditorSchema | null>(null);
  const [matrix, setMatrix] = useState<RoomMatrix | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failedField, setFailedField] = useState<string | null>(null);
  const [source, setSource] = useState('all');
  const [metric, setMetric] = useState<Metric>('share');
  const [query, setQuery] = useState('');
  const [cell, setCell] = useState<{ module: string; itemId: string } | null>(null);
  const [edits, setEdits] = useState<Edits>({});
  const [draftVersion, setDraftVersion] = useState(0);
  const [note, setNote] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const request = useRef(0);

  // Бюджеты и черновик — один раз и порознь: без черновика (не настроено
  // хранилище, сбой) редактор всё равно показывает матрицу, только без
  // сохранённых правок — об этом говорит строка рядом с кнопками.
  useEffect(() => {
    let alive = true;
    api<{ budgets: Array<{ name: string; displayName: string | null }> }>('/api/budgets')
      .then((list) => {
        if (!alive) return;
        setBudgets(list.budgets ?? []);
        setBudget((current) => current ?? list.budgets?.[0]?.name ?? null);
      })
      .catch((cause: Error) => alive && setError(cause.message));
    api<{ version?: number; edits?: Edits; updatedBy?: string }>('/api/draft')
      .then((draft) => {
        if (!alive) return;
        setEdits(draft.edits ?? {});
        setDraftVersion(draft.version ?? 0);
        if (draft.updatedBy) setNote(`Черновик сохранил ${draft.updatedBy}`);
      })
      .catch((cause: Error) => alive && setNote(`Черновик недоступен: ${cause.message}`));
    return () => { alive = false; };
  }, []);

  // Схема бюджета: поля-рычаги, справочники предметов и комнат.
  useEffect(() => {
    if (!budget) return;
    let alive = true;
    setSchema(null);
    setMatrix(null);
    setCell(null);
    request.current += 1;
    api<unknown>(`/api/schema?budget=${encodeURIComponent(budget)}`)
      .then((payload) => alive && setSchema(normalizeSchema(payload)))
      .catch((cause: Error) => alive && setError(cause.message));
    return () => { alive = false; };
  }, [budget]);

  // Пересчёт матрицы симуляцией после паузы в правках.
  const overrides = useMemo(() => (schema ? overridesFor(schema, edits) : {}), [schema, edits]);
  const overridesKey = JSON.stringify(overrides);
  useEffect(() => {
    if (!schema || !budget || schema.budget !== budget) return;
    const id = ++request.current;
    const timer = window.setTimeout(() => {
      setPending(true);
      api<unknown>('/api/simulate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ budget, runs, overrides }),
      }).then((payload) => {
        if (id !== request.current) return;
        const next = normalizeMatrix(payload);
        setMatrix(next);
        setError(next ? null : 'Сервис не вернул матрицу комнат: нужен spusk-sim с каталогом модулей (CONTRACT.md §1d).');
        setFailedField(null);
      }).catch((cause: Error & { field?: string }) => {
        if (id !== request.current) return;
        setError(cause.message);
        setFailedField(cause.field ?? null);
      }).finally(() => id === request.current && setPending(false));
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // overridesKey — устойчивый ключ правок; сам объект overrides новый на каждой отрисовке.
  }, [schema, budget, runs, overridesKey]);

  const view = useMemo(() => (matrix && schema ? matrixView(matrix, schema, source, metric, query) : null), [matrix, schema, source, metric, query]);
  const lines = useMemo(() => (schema ? changeLines(schema, edits) : []), [schema, edits]);
  const edit = (key: string, value: number | string, original: number | string) => setEdits((current) => withEdit(current, key, value, original));

  const save = async () => {
    setSaving(true);
    setNote(null);
    try {
      const body = await api<{ version: number }>('/api/draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ version: draftVersion, edits }),
      });
      setDraftVersion(body.version);
      setNote('Черновик сохранён. Он доступен админу и Артёму.');
    } catch (cause) {
      setNote(cause instanceof Error ? cause.message : 'Не удалось сохранить черновик.');
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(changesText(lines, email));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const selectedRoom = cell && matrix ? matrix.rooms.find((room) => room.module === cell.module) ?? null : null;
  const roomPassport = cell && schema ? schema.rooms.find((room) => room.module === cell.module) ?? null : null;
  const selectedItem = cell && schema ? schema.items.find((item) => item.id === cell.itemId) ?? { id: cell.itemId, displayName: '', category: null } : null;
  const cellValues = cell && matrix ? matrix.sources.map((src) => ({
    source: src,
    data: matrix.cells.find((entry) => entry.module === cell.module && entry.itemId === cell.itemId && entry.source === src),
  })) : [];
  const bonus = schema ? preferenceBonusField(schema) : null;

  return <main className="studio">
    <header className="studio-heading">
      <div><span className="eyebrow">СПУСК / БАЛАНС</span><h1>Распределение предметов по комнатам</h1>
        <p>Каждая клетка — что игра положит в эту комнату, по настоящей генерации этажа на {runs.toLocaleString('ru-RU')} забегах. Нажмите на клетку, чтобы увидеть, из чего складывается шанс, и поменять рычаги.</p></div>
      <div className="studio-actions">
        <span className="draft-count">{note ?? (lines.length ? `${lines.length} правок в черновике` : 'Нет правок')}</span>
        <button className="secondary-button" disabled={!lines.length} onClick={() => setEdits({})}>Сбросить</button>
        <button className="secondary-button" disabled={!lines.length} onClick={copy}>{copied ? 'Скопировано' : 'Скопировать было → стало'}</button>
        <button className="primary-button" disabled={saving} onClick={save}>{saving ? 'Сохраняю…' : 'Сохранить черновик'}</button>
      </div>
    </header>

    <div className="matrix-toolbar">
      <label>Этаж (бюджет)<select value={budget ?? ''} onChange={(event) => setBudget(event.target.value)}>
        {budgets.map((entry) => <option key={entry.name} value={entry.name}>{entry.name}</option>)}</select></label>
      <label>Забегов<select value={runs} onChange={(event) => setRuns(Number(event.target.value))}>
        {RUNS.map((value) => <option key={value} value={value}>{value.toLocaleString('ru-RU')}</option>)}</select></label>
      <div className="segmented" role="group" aria-label="Источник">{(matrix?.sources ?? ['all']).map((src) =>
        <button key={src} aria-pressed={source === src} onClick={() => setSource(src)}>{SOURCE_LABELS[src] ?? src}</button>)}</div>
      <div className="segmented" role="group" aria-label="Что показывать">{METRICS.map((entry) =>
        <button key={entry.id} title={entry.hint} aria-pressed={metric === entry.id} onClick={() => setMetric(entry.id)}>{entry.label}</button>)}</div>
      <input className="studio-search" placeholder="Найти предмет…" value={query} onChange={(event) => setQuery(event.target.value)} />
      <span className={pending ? 'matrix-status busy' : 'matrix-status'}>{pending ? 'считается…' : matrix ? `готово · ${matrix.runs.toLocaleString('ru-RU')} забегов` : ''}</span>
    </div>

    {error && <div className="studio-notice error">{failedField ? <><b>{failedField}</b>: </> : null}{error}</div>}
    {!view && !error && <div className="studio-notice">Считаю распределение…</div>}

    {view && schema && <section className="matrix-layout">
      <div className={`matrix-scroll${pending ? ' stale' : ''}`}>
        <table className="room-matrix">
          <thead><tr><th className="corner">Комната <small>выпадает</small></th>
            {view.items.map((item) => <th key={item.id} title={item.id}><span>{itemLabel(item)}</span></th>)}</tr></thead>
          <tbody>{view.rooms.map((room) => <tr key={room.module}>
            <th title={room.module}>{room.displayName}<small>{pct(room.presentShare)}</small></th>
            {view.items.map((item) => {
              const value = view.value(room.module, item.id);
              const selected = cell?.module === room.module && cell.itemId === item.id;
              const alpha = view.max > 0 ? 0.08 + 0.82 * (value / view.max) : 0;
              return <td key={item.id} className={selected ? 'selected' : undefined}
                style={value > 0 ? { background: `rgb(37 95 219 / ${alpha.toFixed(3)})`, color: alpha > 0.55 ? 'white' : undefined } : undefined}
                onClick={() => setCell({ module: room.module, itemId: item.id })}>{formatValue(value, metric)}</td>;
            })}</tr>)}</tbody>
        </table>
        {view.items.length === 0 && <div className="studio-notice">В этом срезе ничего не выпало.</div>}
      </div>

      <aside className="matrix-detail">
        {!cell && <div className="detail-empty">Выберите клетку — здесь появится разбор шанса и рычаги.</div>}
        {cell && selectedItem && <>
          <div className="eyebrow">РАЗБОР КЛЕТКИ</div>
          <h2>{itemLabel(selectedItem)} <small>в</small> {selectedRoom?.displayName ?? cell.module}</h2>
          <dl className="detail-facts">
            <dt>Комната выпадает</dt><dd>{selectedRoom ? pct(selectedRoom.presentShare) : '—'} забегов{selectedRoom && selectedRoom.perRun > 1.05 ? ` · ${selectedRoom.perRun.toFixed(1)} шт. на забег` : ''}</dd>
            <dt>Мест в мебели</dt><dd>{roomPassport?.lootPoints ?? '—'}</dd>
            <dt>Категория предмета</dt><dd>{CATEGORY_LABELS[selectedItem.category ?? ''] ?? selectedItem.category ?? '—'}</dd>
          </dl>
          <table className="detail-sources"><thead><tr><th>Источник</th><th>Шанс за забег</th><th>Если комната есть</th><th>Среднее</th></tr></thead>
            <tbody>{cellValues.map(({ source: src, data }) => <tr key={src} className={src === 'all' ? 'total' : undefined}>
              <td>{SOURCE_LABELS[src] ?? src}</td><td>{data ? pct(data.share) : '—'}</td><td>{data ? pct(data.shareWhenPresent) : '—'}</td><td>{data ? data.mean.toFixed(2) : '—'}</td></tr>)}</tbody></table>

          <h3>Какой предмет из категории</h3>
          {weightLevers(schema, cell.itemId, edits).map(({ field, table, shareInTable }) =>
            <div key={field.key}><FieldSlider field={field} edits={edits} onEdit={edit} failed={failedField === field.key}
              label={`Вес в ${table.replace('LootTableDef_', 'таблице ')}`} />
              <p className="lever-note">Шанс внутри таблицы: <b>{pct(shareInTable)}</b> при одном выборе.</p></div>)}
          {weightLevers(schema, cell.itemId, edits).length === 0 && <p className="lever-note">Предмета нет в таблицах лута пола — он приходит из других источников.</p>}

          <h3>Сколько находок категории на этаже</h3>
          {ammoLevers(schema, selectedItem.category).length > 0 && <>
            <p className="lever-note">Число патронов на полу считается от врагов: потолок = врагов × расход на врага, выкладывается ‰ от потолка, дальше делится на пачки.</p>
            {ammoLevers(schema, selectedItem.category).map((field) =>
              <FieldSlider key={field.key} field={field} edits={edits} onEdit={edit} failed={failedField === field.key}
                label={field.key.endsWith('_ammoBudgetPermille') ? 'доля от потолка, ‰ (0 — патронов на полу нет)' : 'расход на врага (потолок = врагов × это)'} />)}</>}
          {budgetLevers(schema, selectedItem.category).map((field) =>
            <FieldSlider key={field.key} field={field} edits={edits} onEdit={edit} failed={failedField === field.key}
              label={field.label.replace(/^lootRows\[\d+\]\./, '').replace('stackMin', 'пачка от').replace('stackMax', 'пачка до').replace(/^min$/, 'находок минимум').replace(/^max$/, 'находок максимум')} />)}
          {budgetLevers(schema, selectedItem.category).length === 0 && <p className="lever-note">Строки бюджета этой категории схема не назвала.</p>}

          {dropStackLevers(schema, cell.itemId).length > 0 && <>
            <h3>Пачка с трупа</h3>
            {dropStackLevers(schema, cell.itemId).map((field) =>
              <FieldSlider key={field.key} field={field} edits={edits} onEdit={edit} failed={failedField === field.key}
                label={field.key.endsWith('_stackMin') ? 'пачка от' : 'пачка до'} />)}</>}

          {roomPassport && <>
            <h3>Предпочтение комнаты</h3>
            <label className={`lever-select${failedField === roomPassport.preferredCategoryKey ? ' failed' : ''}`}>Комната просит категорию
              <select value={currentCategory(roomPassport, edits)}
                onChange={(event) => edit(roomPassport.preferredCategoryKey, event.target.value, roomPassport.preferredCategory)}>
                {['', ...schema.lootCategories].map((id) => <option key={id} value={id}>{CATEGORY_LABELS[id] ?? id}</option>)}
              </select></label>
            {bonus && <FieldSlider field={bonus} edits={edits} onEdit={edit} failed={failedField === bonus.key}
              label="Сила предпочтения (общая для этажа)" />}
          </>}
        </>}
      </aside>
    </section>}

    {lines.length > 0 && <section className="matrix-changes"><h3>Было → стало</h3>
      <table><tbody>{lines.map((line) => <tr key={line.key}><td className="lever-key">{line.key}</td>
        <td>{line.was === null ? '—' : line.was === '' ? '«нет»' : String(line.was)}</td><td><b>{line.now === '' ? '«нет»' : String(line.now)}</b></td>
        <td><button className="secondary-button small" onClick={() => setEdits((current) => { const next = { ...current }; delete next[line.key]; return next; })}>вернуть</button></td></tr>)}</tbody></table>
    </section>}
  </main>;
}
